import { useEffect, useRef, useState } from 'react';
import { createChart, LineSeries } from 'lightweight-charts';
import { getValidToken } from '../utils/auth';
import {
  SESSION_TF, SessionBandPrimitive, buildSessionBands,
  DayLinePrimitive, buildDayLines,
  baseChartOptions, TfButtons, SessionLegend, attachMousePan,
} from '../utils/chartUtils';

const API_URL = process.env.REACT_APP_API_URL || '';

const SECTORS = [
  { key: 'it',      label: 'Info Tech',    color: '#6366f1' },
  { key: 'comms',   label: 'Comms',        color: '#3b82f6' },
  { key: 'fin',     label: 'Financials',   color: '#f59e0b' },
  { key: 'staples', label: 'Con Staples',  color: '#22c55e' },
  { key: 'disc',    label: 'Con Disc',     color: '#ec4899' },
  { key: 'ind',     label: 'Industrials',  color: '#94a3b8' },
  { key: 'health',  label: 'Health Care',  color: '#06b6d4' },
  { key: 'mat',     label: 'Materials',    color: '#84cc16' },
  { key: 'util',    label: 'Utilities',    color: '#a855f7' },
  { key: 'energy',  label: 'Energy',       color: '#f97316' },
  { key: 're',      label: 'Real Estate',  color: '#ef4444' },
];

function twToLineWidth(tw) {
  if (tw >= 20) return 4;
  if (tw >= 12) return 3;
  if (tw >= 6)  return 2;
  return 1;
}

function splitSectors(points) {
  const out = {};
  for (const s of SECTORS) out[s.key] = [];
  for (const p of points) {
    for (const s of SECTORS) {
      if (p[s.key] !== undefined) {
        out[s.key].push({ time: p.time, value: p[s.key] });
      }
    }
  }
  return out;
}

export default function SectorSeriesChart({ tf, setTf }) {
  const [sectorWeights, setSectorWeights] = useState(null);

  const containerRef = useRef(null);
  const chartRef     = useRef(null);
  const seriesRefs   = useRef({});
  const primitiveRef = useRef(null);
  const dayLineRef   = useRef(null);
  const pollRef      = useRef(null);
  const mountedRef   = useRef(true);
  const initialDone  = useRef(false);

  useEffect(() => {
    mountedRef.current = true;

    const opts = baseChartOptions(containerRef.current.clientWidth || 300);
    opts.height = 300;
    const chart = createChart(containerRef.current, opts);
    chartRef.current = chart;

    const refs = {};
    for (const s of SECTORS) {
      const series = chart.addSeries(LineSeries, {
        color: s.color,
        lineWidth: 2,
        priceLineVisible: false,
        lastValueVisible: true,
        crosshairMarkerVisible: true,
        priceFormat: {
          type: 'custom',
          minMove: 0.01,
          formatter: v => (v >= 0 ? '+' : '') + v.toFixed(2) + '%',
        },
      });
      refs[s.key] = series;
    }
    seriesRefs.current = refs;

    chart.priceScale('right').applyOptions({ mode: 0 });

    const primitive = new SessionBandPrimitive();
    primitiveRef.current = primitive;
    refs['it'].attachPrimitive(primitive);

    const dayLine = new DayLinePrimitive();
    dayLineRef.current = dayLine;
    refs['it'].attachPrimitive(dayLine);

    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width;
      if (w > 0 && mountedRef.current && chartRef.current) {
        chartRef.current.applyOptions({ width: w });
      }
    });
    ro.observe(containerRef.current);

    const detachPan = attachMousePan(containerRef.current, chart);

    return () => {
      mountedRef.current = false;
      detachPan();
      ro.disconnect();
      clearInterval(pollRef.current);
      chart.remove();
      chartRef.current = null;
      seriesRefs.current = {};
      primitiveRef.current = null;
      dayLineRef.current = null;
    };
  }, []);

  useEffect(() => {
    initialDone.current = false;
    clearInterval(pollRef.current);

    const fetchSeries = async (isInitial) => {
      if (!mountedRef.current || !chartRef.current) return;
      try {
        const token = await getValidToken();
        if (!token || !mountedRef.current) return;

        const res = await fetch(`${API_URL}/api/chart/sector-series?tf=${tf}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok || !mountedRef.current) return;

        const data = await res.json();
        const points = data.series || [];
        const sw     = data.sectorWeights || {};

        if (!mountedRef.current) return;

        setSectorWeights(sw);

        for (const s of SECTORS) {
          const series = seriesRefs.current[s.key];
          if (!series) continue;
          series.applyOptions({ lineWidth: twToLineWidth(sw[s.key] || 0) });
        }

        if (!points.length) return;

        const split = splitSectors(points);

        if (isInitial || !initialDone.current) {
          for (const s of SECTORS) {
            seriesRefs.current[s.key]?.setData(split[s.key]);
          }
          chartRef.current.timeScale().scrollToRealTime();
          initialDone.current = true;
        } else {
          for (const s of SECTORS) {
            const pts = split[s.key];
            if (pts.length) seriesRefs.current[s.key]?.update(pts[pts.length - 1]);
          }
        }

        if (SESSION_TF.has(tf)) {
          primitiveRef.current?.setBands(buildSessionBands(points));
        } else {
          primitiveRef.current?.clearBands();
        }

        dayLineRef.current?.setLines(buildDayLines(points));
      } catch { /* show stale data */ }
    };

    fetchSeries(true);
    pollRef.current = setInterval(() => fetchSeries(false), 5000);
    return () => clearInterval(pollRef.current);
  }, [tf]);

  return (
    <div className="bg-gray-900 rounded-2xl p-5 mb-5">
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <span className="text-xs font-bold px-3 py-1.5 rounded border"
          style={{ backgroundColor: '#111827', color: '#e5e7eb', borderColor: '#374151' }}>
          Sector Series
        </span>
        <TfButtons tf={tf} setTf={setTf} />
        {SESSION_TF.has(tf) && <SessionLegend />}
      </div>

      {/* Sector legend */}
      <div className="flex items-center gap-x-4 gap-y-1.5 flex-wrap mb-3">
        {SECTORS.map(s => (
          <span key={s.key} className="flex items-center gap-1.5 text-xs text-gray-400">
            <span className="inline-block rounded"
              style={{
                backgroundColor: s.color,
                width: 16,
                height: twToLineWidth(sectorWeights?.[s.key] || 0) + 1,
              }} />
            {s.label}
            {sectorWeights?.[s.key] != null && (
              <span className="text-gray-500">({sectorWeights[s.key].toFixed(1)}%)</span>
            )}
          </span>
        ))}
      </div>

      <div ref={containerRef} className="relative" />
    </div>
  );
}
